import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RefreshCw,
  ChevronDown,
  FileText,
  Tag,
  Calendar,
  User,
  Star,
  Edit,
  Trash2,
  X,
  Plus,
  Save,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";

export default function QuizTopics() {
  const [topics, setTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [newTopic, setNewTopic] = useState({
    topic: "",
    subjectId: "",
  });

  // Fetch initial data
  useEffect(() => {
    fetchSubjects();
    fetchTopics();
  }, []);

  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/quizSubject/all`);
      setSubjects(data.data || []);
    } catch (error) {
      console.error("Error fetching quiz subjects:", error);
    }
  };

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/quizTopic/all`);
      setTopics(data.data || []);
    } catch (error) {
      // toast.error("Failed to fetch quiz topics");
      console.error("Error fetching quiz topics:", error);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.topic.trim()) {
      toast.error("Please enter a topic name");
      return;
    }
    if (!newTopic.subjectId) {
      toast.error("Please select a subject");
      return;
    }

    try {
      setSubmitting(true);
      // Assuming endpoint, check your API docs
      const { data } = await axios.post(
        `${server}/quizTopic/new`,
        {
          topic: newTopic.topic.trim(),
          subjectId: parseInt(newTopic.subjectId),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("Topic added successfully!");
        resetNewTopic();
        setShowAddModal(false);
        fetchTopics();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add topic");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTopic = async (topic) => {
    if (!newTopic.topic.trim()) {
      toast.error("Please enter a topic name");
      return;
    }
    if (!newTopic.subjectId) {
      toast.error("Please select a subject");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(
        `${server}/quizTopic/updateById`,
        {
          id: topic.id,
          topic: newTopic.topic.trim(),
          subjectId: parseInt(newTopic.subjectId),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("Topic updated successfully!");
        setEditingTopic(null);
        resetNewTopic();
        fetchTopics();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update topic");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm("Are you sure you want to delete this topic?")) {
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.delete(
        `${server}/quizTopic/deleteById/${topicId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("Topic deleted successfully!");
        fetchTopics();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete topic");
    } finally {
      setSubmitting(false);
    }
  };

  const resetNewTopic = () => {
    setNewTopic({
      topic: "",
      subjectId: "",
    });
  };

  const startEdit = (topic) => {
    setEditingTopic(topic);
    setNewTopic({
      topic: topic.topic || topic.title, // Handle potential variations
      subjectId: topic.subjectId?.toString(),
    });
  };

  const cancelEdit = () => {
    setEditingTopic(null);
    resetNewTopic();
  };

  // Filter topics
  const filteredTopics = topics.filter((topic) => {
    const matchesSearch = (topic.topic || topic.title || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesSubject = selectedSubjectId
      ? topic.subjectId === parseInt(selectedSubjectId)
      : true;

    return matchesSearch && matchesSubject;
  });

  const getSubjectName = (subjectId) => {
    const subject = subjects.find((s) => s.id === subjectId);
    return subject ? subject.title || subject.subject : "Unknown Subject";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Quiz Topics</h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Manage your quiz topics and link them to subjects.
          </p>
        </motion.div>

        {/* Filters Section */}
        <div className="bg-gray-800 rounded-2xl p-6 mb-6 shadow-lg border border-gray-700">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-4">
            {/* Search */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={fetchTopics}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-3 bg-gray-700 text-gray-300 border border-gray-600 rounded-xl hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                Add Topic
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subject Filter
              </label>
              <div className="relative">
                <select
                  value={selectedSubjectId}
                  onChange={(e) => setSelectedSubjectId(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="">All Subjects</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.title || subject.subject}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Topics List */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : filteredTopics.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-2xl border border-gray-700">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                No topics found
              </h3>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredTopics.map((topic) => (
                <motion.div
                  key={topic.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`bg-gray-800 rounded-2xl p-6 border transition-all duration-200 border-gray-700 hover:border-gray-600`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Topic Info */}
                    <div className="flex-1">
                      {editingTopic?.id === topic.id ? (
                        <div className="space-y-4">
                          <input
                            type="text"
                            value={newTopic.topic}
                            onChange={(e) =>
                              setNewTopic({
                                ...newTopic,
                                topic: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Topic name..."
                            autoFocus
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="relative">
                              <select
                                value={newTopic.subjectId}
                                onChange={(e) =>
                                  setNewTopic({
                                    ...newTopic,
                                    subjectId: e.target.value,
                                  })
                                }
                                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                              >
                                <option value="">Select Subject</option>
                                {subjects.map((subject) => (
                                  <option key={subject.id} value={subject.id}>
                                    {subject.title || subject.subject}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-semibold text-white">
                              {topic.topic || topic.title}
                            </h3>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                            <div className="flex items-center gap-1">
                              <Tag className="w-4 h-4" />
                              <span>
                                Subject: {getSubjectName(topic.subjectId)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(topic.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {editingTopic?.id === topic.id ? (
                        <>
                          <button
                            onClick={() => handleEditTopic(topic)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(topic)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTopic(topic.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Add Topic Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">
                  Add New Quiz Topic
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleAddTopic} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Topic Name
                  </label>
                  <input
                    type="text"
                    value={newTopic.topic}
                    onChange={(e) =>
                      setNewTopic({ ...newTopic, topic: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter topic name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Subject
                  </label>
                  <div className="relative">
                    <select
                      value={newTopic.subjectId}
                      onChange={(e) =>
                        setNewTopic({ ...newTopic, subjectId: e.target.value })
                      }
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.title || subject.subject}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                </div>

                <div className="pt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                  >
                    {submitting ? "Adding..." : "Add Topic"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
